import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosControlTowerService, RorosSavedViewService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type {
  CreateWorkItemPayload,
  UpdateWorkItemPayload,
  TransitionWorkItemPayload,
  CreateLinkPayload,
} from '@/types/roros/control-tower';
import type {
  CreateSavedViewPayload,
  SavedViewScope,
  UpdateSavedViewPayload,
} from '@/types/roros/saved-views';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosWorkItems(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.controlTower.workItems, params] as const,
    queryFn: () => RorosControlTowerService.listWorkItems(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosWorkItem(id: string) {
  return useQuery({
    queryKey: rorosKeys.controlTower.workItem(id),
    queryFn: () => RorosControlTowerService.getWorkItem(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosWorkItemHistory(id: string) {
  return useQuery({
    queryKey: rorosKeys.controlTower.history(id),
    queryFn: () => RorosControlTowerService.getHistory(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosWipStatus() {
  return useQuery({
    queryKey: rorosKeys.controlTower.wipStatus,
    queryFn: () => RorosControlTowerService.getWipStatus(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosControlTowerOverview() {
  return useQuery({
    queryKey: rorosKeys.controlTower.overview,
    queryFn: () => RorosControlTowerService.getOverview(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosVerificationInbox() {
  return useQuery({
    queryKey: rorosKeys.controlTower.verificationInbox,
    queryFn: () => RorosControlTowerService.getVerificationInbox(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosWorkItemCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWorkItemPayload) => RorosControlTowerService.createWorkItem(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItems });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.overview });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.wipStatus });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosWorkItemUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWorkItemPayload }) =>
      RorosControlTowerService.updateWorkItem(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItems });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItem(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.overview });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosWorkItemTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TransitionWorkItemPayload }) =>
      RorosControlTowerService.transitionWorkItem(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItems });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItem(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.history(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.overview });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.wipStatus });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.verificationInbox });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

/**
 * v1.1.19 — Hard delete a work item (and all descendants).
 * Permission gate: route requires `roros.control-tower.delete`. UI gates on
 * the same permission via usePermissions; this hook does not check it.
 */
export function useRorosWorkItemHardDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosControlTowerService.hardDeleteWorkItem(id),
    onSuccess: (_data, id) => {
      // Belt-and-suspenders alongside the WS event invalidation in
      // useRorosSocket — cover the case where the WS is briefly disconnected.
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItems });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItem(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.overview });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.wipStatus });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.verificationInbox });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosWorkItemCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateLinkPayload }) =>
      RorosControlTowerService.createLink(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItem(id) });
    },
  });
}

export function useRorosWorkItemDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, linkId }: { id: string; linkId: string }) =>
      RorosControlTowerService.deleteLink(id, linkId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItem(id) });
    },
  });
}

// ─── J7-W5 task 6c-1: sub-items / parent-child rollup ─────────────────────

export function useRorosWorkItemTree(id: string, enabled = true) {
  return useQuery({
    queryKey: rorosKeys.controlTower.workItemTree(id),
    queryFn: () => RorosControlTowerService.getWorkItemTree(id),
    enabled: !!id && enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosWorkItemRollup(id: string, enabled = true) {
  return useQuery({
    queryKey: rorosKeys.controlTower.workItemRollup(id),
    queryFn: () => RorosControlTowerService.getRollupStats(id),
    enabled: !!id && enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── v1.1.20: iteration memory ─────────────────────────────────────────────

/** Full iteration record (every deliverable version + paired QC report). */
export function useRorosWorkItemIterations(id: string, enabled = true) {
  return useQuery({
    queryKey: rorosKeys.controlTower.workItemIterations(id),
    queryFn: () => RorosControlTowerService.getIterations(id),
    enabled: !!id && enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── J7-W5 task 6c-2: saved views ─────────────────────────────────────────

export function useRorosSavedViews(scope: SavedViewScope = 'control_tower') {
  return useQuery({
    queryKey: rorosKeys.savedViews.list(scope),
    queryFn: () => RorosSavedViewService.list(scope),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSavedViewCreate(scope: SavedViewScope = 'control_tower') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSavedViewPayload) => RorosSavedViewService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.savedViews.list(scope) });
    },
  });
}

export function useRorosSavedViewUpdate(scope: SavedViewScope = 'control_tower') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSavedViewPayload }) =>
      RorosSavedViewService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.savedViews.list(scope) });
    },
  });
}

export function useRorosSavedViewDelete(scope: SavedViewScope = 'control_tower') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosSavedViewService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.savedViews.list(scope) });
    },
  });
}
